import React, { useEffect, useState } from "react";
import Game from "./pages/Game";
import EndPage from "./pages/EndPage";
import CustomLogin from "./pages/CustomLogin";
import QuestionSelect from "./pages/QuestionSelect";
import { Routes, Route, useNavigate } from "react-router-dom";
import PasswordPage from "./pages/PasswordPage";
import { getStoredTeamActions, setStoredTeamActions } from "./utils/storage";
import axios from "axios";

const loadFontFace = (family, url, weight = "400") => {
    if (!url || url === "false" || typeof url !== "string") return;

    const face = new FontFace(family, `url(${url})`, { weight });
    face.load()
        .then((loadedFace) => {
            const fontSet = document["fonts"];
            if (fontSet && typeof fontSet["add"] === "function") {
                fontSet["add"](loadedFace);
            }
        })
        .catch(() => {});
};

const loadFontFamily = (family, config = {}) => {
    loadFontFace(family, config.regular_font, "400");
    loadFontFace(family, config.medium_font, "500");
    loadFontFace(family, config.bold_font, "700");
    loadFontFace(family, config.extra_bold_font, "800");
};

const CustomLoginRoute = ({ onLoginSuccess }) => {
    return (
        <CustomLogin
            onLoginSuccess={() => {
                if (onLoginSuccess) onLoginSuccess();
            }}
        />
    );
};

const QuestionSelectRoute = () => {
    const navigate = useNavigate();
    return (
        <QuestionSelect
            onComplete={() => {
                navigate("/intro");
            }}
        />
    );
};

const IntroRoute = ({
    addWheelData,
    onLogin,
    addTeamAction,
    resetTeamActions,
    isIframe,
}) => {
    const navigate = useNavigate();
    return (
        <Game
            key="intro-game"
            mode="intro"
            addWheelData={addWheelData}
            login={onLogin}
            addTeamAction={addTeamAction}
            resetTeamActions={resetTeamActions}
            isIframe={isIframe}
            onGameStart={() => navigate("/game")}
            onFinish={() => navigate("/end-page")}
        />
    );
};

const GameRoute = ({
    addWheelData,
    onLogin,
    addTeamAction,
    resetTeamActions,
    isIframe,
}) => {
    const navigate = useNavigate();
    return (
        <Game
            key="main-game"
            mode="game"
            addWheelData={addWheelData}
            login={onLogin}
            addTeamAction={addTeamAction}
            resetTeamActions={resetTeamActions}
            isIframe={isIframe}
            onFinish={() => navigate("/end-page")}
        />
    );
};

const App = ({ isIframe }) => {
    const navigate = useNavigate();
    const [teamAction, setTeamAction] = useState(() =>
        getStoredTeamActions([]),
    );
    const [wheelData, setWheelData] = useState([]);
    const [onLogin, setLogin] = useState(false);

    const clearSession = () => {
        const baseUrl = window.themeObj.restURL;
        const apiURL = `${baseUrl}/game/reset-game-progress`;
        const finalTeamActions = getStoredTeamActions([]);
        const gameProgressId = window?.themes?.user?.current_game_progress;
        axios
            .post(apiURL, {
                final_team_actions: finalTeamActions,
                game_progress_id: gameProgressId,
            })
            .then(() => {
                setTeamAction([]);
                setWheelData([]);
                localStorage.removeItem("teamconnector3_selected_categories");
                localStorage.removeItem("teamconnector3_team_members");
                localStorage.removeItem("teamconnector3_session_timer");
                localStorage.removeItem("teamconnector3_remaining_seconds");
                localStorage.removeItem("teamconnector3_is_game_active");
                localStorage.removeItem("teamconnector3_team_actions");
                navigate("/question-select");
            })
            .catch((error) => {
                console.error(error);
            });
    };

    useEffect(() => {
        setStoredTeamActions(teamAction);
    }, [teamAction]);

    const addTeamAction = (data) => {
        setTeamAction((prev) => {
            const next = Array.isArray(data)
                ? [...prev, ...data]
                : [...prev, data];
            setStoredTeamActions(next);
            return next;
        });
    };

    const resetTeamActions = () => {
        setTeamAction([]);
        setStoredTeamActions([]);
    };

    const addWheelData = (data) => {
        setWheelData([...wheelData, data]);
    };

    const Login = () => {
        setLogin(true);
        window.location.href = window.login_page?.login_url;
    };

    const deleteTeamAction = (data) => {
        var global = [...teamAction];

        for (var i = 0; i < global.length; i++) {
            if (global[i] === data) {
                global.splice(i, 1);
                setTeamAction(global);
                setStoredTeamActions(global);
                break;
            }
        }
    };

    const updateTeamAction = (data, index = null) => {
        var global = [...teamAction];
        if (index !== null && index >= 0 && index < global.length) {
            global[index] = data;
        } else {
            global.push(data);
        }
        setTeamAction(global);
        setStoredTeamActions(global);
    };

    useEffect(() => {
        const globalFonts = window["fonts"] || {};
        const headingConfig = globalFonts.heading || {};
        const bodyConfig = globalFonts.body || {};

        loadFontFamily("Custom Fonts Heading", headingConfig);
        loadFontFamily("Custom Fonts Body", bodyConfig);
    }, []);

    const defaultRoutes = [
        <Route key="home" path="/" element={<QuestionSelectRoute />} />,
        <Route
            key="question-select"
            path="/question-select"
            element={<QuestionSelectRoute />}
        />,
        <Route
            key="intro"
            path="/intro"
            element={
                <IntroRoute
                    addWheelData={addWheelData}
                    onLogin={onLogin}
                    addTeamAction={addTeamAction}
                    resetTeamActions={resetTeamActions}
                    isIframe={isIframe}
                />
            }
        />,
        <Route
            key="game"
            path="/game"
            element={
                <GameRoute
                    addWheelData={addWheelData}
                    onLogin={onLogin}
                    addTeamAction={addTeamAction}
                    resetTeamActions={resetTeamActions}
                    isIframe={isIframe}
                />
            }
        />,
        <Route
            key="end-page"
            path="/end-page"
            element={
                <EndPage
                    deleteTeamAction={deleteTeamAction}
                    updateTeamAction={updateTeamAction}
                    teamAction={teamAction}
                    clearSession={clearSession}
                />
            }
        />,
        <Route
            key="password-page"
            path="/password-page"
            element={<PasswordPage wheelData={wheelData} login={Login} />}
        />,
        <Route
            key="login"
            path="/login"
            element={<CustomLoginRoute onLoginSuccess={Login} />}
        />,
    ];

    return (
        <Routes>
            <React.Fragment>{defaultRoutes}</React.Fragment>
        </Routes>
    );
};

export default App;
